import RenderTokenIcon from '@components/shared/RenderTokenIcon'
import commonPoolStore from '@store/commonPoolStore'
import { commonPoolContracts } from 'config/contracts'
import { useCommonPoolAPR } from 'hooks/vault/useCommonPoolAPR'
import { useMemo } from 'react'
import { cn } from 'utils/classnames'
import Tooltip from '@components/shared/Tooltip'
import InfoIcon from 'public/icons/info.svg'
import { getProvider } from 'rfx/lib/rpc'
import CommonPool from 'abis/CommonPool.json'
import useSWR from 'swr'
import { formatPrice } from 'apis/birdeye/helpers'
import { SONIC } from 'config/chains'
import { getToken, getTokenBySymbol } from 'config/tokens'
import { getFormattedData } from 'hooks/stats/useVolumeStats'
import commonStore from '@store/commonStore'
import SelectIcon from 'public/icons/select.svg'
import { BigNumber, Contract } from 'utils/ethersImport'

export const VaultInfoCard = ({
  info: item,
  chainId,
}: {
  info: { label: string; value: string }
  chainId: number
}) => {
  const setSelectedVault = commonPoolStore((store) => store.setSelectedVault)

  const selectedVault = commonPoolStore((store) => store.selectedVault)
  const commonPoolAsset = selectedVault.label
  const { apr } = useCommonPoolAPR()
  const provider = getProvider(undefined, chainId)
  const prices = commonStore((state) => state.prices)

  const tokens = useMemo(
    () =>
      commonPoolContracts[chainId].find((pool) => pool.label === item.label)
        ?.tokens,
    [item.label, chainId],
  )
  const tokenInfo = useMemo(
    () => getToken(chainId, item.value),
    [item.value, chainId],
  )

  const contract = new Contract(item.value, CommonPool.abi, provider)

  const { data, isLoading: loading } = useSWR(
    `vault-info-${item.value}-${chainId}`,
    {
      fetcher: async () => {
        try {
          const totalAssets = await contract.totalAssets()
          const totalSupply = await contract.totalSupply()
          const symbol = await contract.symbol()
          const name = await contract.name()
          const decimals = await contract.decimals()

          const convertToAssets = await contract.convertToAssets(
            Math.pow(10, 18).toString(), // decimals for share to asset price
          )
          return {
            totalAssets,
            totalSupply,
            convertToAssets,
            symbol,
            name,
            decimals,
          }
        } catch (e) {
          return {
            totalAssets: 0,
            totalSupply: 0,
            convertToAssets: 0,
            symbol: '',
            name: '',
            decimals: 18,
          }
        }
      },
    },
  )

  const tvl = useMemo(() => {
    const token = getTokenBySymbol(chainId, item.label)?.pythId || ''

    if (!prices[token]) {
      return 0
    }
    return (
      getFormattedData(
        BigNumber.from(data?.totalAssets || 0)
          .mul(prices[token]?.maxPrice)
          .toString(),
      ) || 0
    )
  }, [data?.totalAssets, prices, item.label, chainId])

  return (
    <div
      className={cn(
        `noise relative flex h-auto w-full min-w-[280px] cursor-pointer flex-col gap-4 overflow-hidden rounded-sm bg-patek-neutral-celestial-grey-1000 p-4 outline outline-1 outline-offset-[-1px] lg:w-1/2 lg:max-w-[396px] lg:gap-10 lg:rounded-sm lg:bg-patek-neutral-celestial-grey-1000`,
        `${selectedVault.label === item.label ? '!bg-[linear-gradient(180deg,_#1D897A_-244.57%,_#000_100%)] outline-th-active [box-shadow:0_4px_6px_rgba(0,_0,_0,_0.1)]' : 'outline-patek-neutral-celestial-grey-800'} `,
      )}
      onClick={() => setSelectedVault({ label: item.label, value: item.value })}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <RenderTokenIcon
            symbol={item.label.toLowerCase()}
            size="largest"
            state={item.label === commonPoolAsset ? 'active' : 'hover'}
          />
          <div className="hidden items-center justify-start gap-2 text-2xl font-medium leading-normal text-patek-primary-celestial-blue-800 lg:flex">
            {tokens && tokens?.length > 0
              ? `${tokens?.[0]} / ${item.label}`
              : `${item.label}`}
            {tokens && tokens?.length > 0 && (
              <Tooltip
                content={
                  <>
                    {tokens?.[0]} is auto-swapped {item.label} into before being
                    deposited into the vault.
                  </>
                }
                placement="right"
                showUnderline={false}
              >
                <InfoIcon />
              </Tooltip>
            )}
          </div>
          <span
            className={cn(
              'block text-base font-medium text-th-fgd-2 lg:hidden',
              item.label === commonPoolAsset && 'text-th-fgd-1',
            )}
          >
            {item.label}
          </span>
        </div>
        <div
          className={cn(
            `flex items-center justify-center gap-2 text-xs font-medium leading-normal lg:hidden fhd:text-sm`,
            item.label !== commonPoolAsset ? 'text-th-fgd-3' : 'text-th-fgd-1',
          )}
        >
          <span className="flex items-center justify-start gap-2 text-base font-medium leading-normal text-patek-primary-celestial-blue-800 lg:hidden">
            {tokens && tokens?.length > 0
              ? `${tokens?.[0]} / ${item.label}`
              : `${item.label}`}
          </span>
          <span className="hidden lg:block">{item.label}</span>
          <div
            className={cn(
              'block lg:hidden',
              item.label === commonPoolAsset ? '[&_path]:fill-th-active' : '',
            )}
          >
            <SelectIcon />
          </div>
        </div>
      </div>

      <div className="flex items-start justify-between gap-4 max-[400px]:gap-2">
        <div className="block lg:hidden">
          <VaultOverviewSection
            label={
              chainId === SONIC ? (
                <div className="flex items-center gap-2">
                  <div>{'Est. APR (1D)'}</div>
                  <Tooltip
                    content={<>APRs will be tracked once trading goes live.</>}
                    placement="top"
                    showUnderline={false}
                  >
                    <InfoIcon />
                  </Tooltip>
                </div>
              ) : (
                <div>APR (7D)</div>
              )
            }
            value={(apr || 0)?.toFixed(2) + '%'}
            isActive={item.label === commonPoolAsset}
          />
        </div>

        <VaultOverviewSection
          label={<div className="lg:text-sm">TVL</div>}
          value={
            loading
              ? '...'
              : '$' +
                formatPrice(
                  parseFloat(
                    (tvl * Math.pow(10, -tokenInfo.decimals)).toString(),
                  ),
                )
          }
          isActive={item.label === commonPoolAsset}
        />
        <VaultOverviewSection
          label={
            <div className="text-patek-neutral-celestial-grey-500 lg:text-sm">
              Total Supply
            </div>
          }
          value={
            loading
              ? '...'
              : `${formatPrice(
                  Number(data?.totalSupply * Math.pow(10, -data?.decimals)),
                  2,
                )} SLV ${item.label}`
          }
          isActive={item.label === commonPoolAsset}
        />
        <VaultOverviewSection
          label={
            <div className="text-patek-neutral-celestial-grey-500 lg:text-sm">
              Price
            </div>
          }
          value={
            loading
              ? '...'
              : formatPrice(data?.convertToAssets * Math.pow(10, -18), 2)
          }
          isActive={item.label === commonPoolAsset}
        />

        <VaultOverviewSection
          label={
            chainId === SONIC ? (
              <div className="flex items-center gap-2">
                <Tooltip
                  content={<>APRs will be tracked once trading goes live.</>}
                  placement="top"
                  showUnderline={false}
                >
                  <div className="flex items-center gap-2 text-sm">
                    Est. APR <span className="text-base">(1D)</span>
                  </div>
                </Tooltip>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm">
                APR <span className="text-sm">(7D)</span>
              </div>
            )
          }
          value={(apr || 0)?.toFixed(2) + '%'}
          isActive={item.label === commonPoolAsset}
        />
      </div>
    </div>
  )
}

export const VaultOverviewSection = ({
  label,
  value,
  isActive,
}: {
  label: string | React.ReactNode
  value: string
  isActive: boolean
}) => {
  return (
    <div className="flex flex-col justify-center gap-2 align-middle">
      <div className="justify-start text-xs font-medium uppercase leading-3 text-patek-neutral-celestial-grey-500 lg:text-xs fhd:text-sm">
        {label}
      </div>
      <div
        className={cn(
          `justify-start text-xs font-medium leading-normal text-th-fgd-1 lg:text-base fhd:text-sm`,
          `${isActive ? 'text-patek-greyscale-text-primary' : 'text-patek-neutral-celestial-grey-500'}`,
        )}
      >
        {value}
      </div>
    </div>
  )
}
